import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, MessageCircle, FileText, Send, Settings, Activity, BarChart3,
  Edit3, Eye, Copy, Power, PowerOff, CheckCircle2, XCircle, Clock, Search,
  AlertTriangle, Save, X, Loader2, ChevronRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const STATUS_BADGE = {
  sent:      { class: 'bg-blue-100 text-blue-700 border-blue-200',         Icon: Send },
  delivered: { class: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  read:      { class: 'bg-purple-100 text-purple-700 border-purple-200',   Icon: Eye },
  failed:    { class: 'bg-red-100 text-red-700 border-red-200',            Icon: XCircle },
  cancelled: { class: 'bg-slate-100 text-slate-600 border-slate-200',      Icon: X },
  skipped:   { class: 'bg-slate-100 text-slate-500 border-slate-200',      Icon: X },
  queued:    { class: 'bg-amber-100 text-amber-700 border-amber-200',      Icon: Clock },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.queued;
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cfg.class}`}>
      <Icon size={11} /> {status?.toUpperCase() || 'PENDING'}
    </span>
  );
}

function StatCard({ label, value, color, Icon }) {
  return (
    <div className={`rounded-2xl p-4 bg-gradient-to-br ${color} text-white shadow-sm`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] uppercase tracking-wider font-semibold opacity-90">{label}</span>
        <Icon size={16} className="opacity-80" />
      </div>
      <div className="text-2xl font-bold">{value ?? '—'}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Template editor dialog
// ─────────────────────────────────────────────────────────────
function TemplateEditor({ tpl, onClose, onSave }) {
  const [draft, setDraft] = useState(() => ({ ...tpl }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      toast.success('Template saved');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="wa-tpl-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 size={18} /> Edit Template — <span className="text-emerald-700">{tpl.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Display Name</label>
              <Input
                value={draft.name || ''}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                data-testid="tpl-edit-name"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">
                AiSensy Campaign Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={draft.campaign_name || ''}
                onChange={(e) => setDraft({ ...draft, campaign_name: e.target.value })}
                placeholder="exact campaign name from AiSensy dashboard"
                data-testid="tpl-edit-campaign-name"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Must match an approved AiSensy campaign exactly.
              </p>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">
              Variables (used in {`{{var}}`} placeholders, comma-separated)
            </label>
            <Input
              value={(draft.variables || []).join(', ')}
              onChange={(e) =>
                setDraft({ ...draft, variables: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })
              }
              data-testid="tpl-edit-vars"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">
              Message Body — use <code className="bg-slate-100 px-1">{`{{customer_name}}`}</code> style placeholders
            </label>
            <Textarea
              rows={14}
              value={draft.body || ''}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              className="font-mono text-[13px]"
              data-testid="tpl-edit-body"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={!!draft.enabled}
                onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
                data-testid="tpl-edit-enabled"
              />
              <span className="font-medium">Enabled</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={!!draft.auto_send}
                onCheckedChange={(v) => setDraft({ ...draft, auto_send: v })}
                data-testid="tpl-edit-autosend"
              />
              <span className="font-medium">Auto-send on event</span>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X size={14} className="mr-1" /> Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Preview dialog (render body with sample context)
// ─────────────────────────────────────────────────────────────
function TemplatePreview({ tpl, onClose }) {
  const sample = useMemo(() => ({
    customer_name: 'Rajesh Sharma',
    booking_id: 'BK260224001',
    service_name: 'Termite Control Treatment',
    city: 'Dehradun, Uttarakhand',
    service_date: '26 Feb 2026 · 9:30 AM',
    estimated_price: '4,500',
    assigned_technician: 'Mr. Vikram (TIPC ID-204)',
    invoice_number: 'TIPC-GST-26-0021',
  }), []);

  const rendered = useMemo(() => {
    let out = tpl.body || '';
    Object.entries(sample).forEach(([k, v]) => {
      out = out.split(`{{${k}}}`).join(v);
    });
    return out;
  }, [tpl, sample]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="wa-tpl-preview">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye size={18} /> WhatsApp Preview
          </DialogTitle>
        </DialogHeader>
        <div className="bg-[#e5ddd5] rounded-xl p-3" style={{
          backgroundImage:
            'radial-gradient(circle at 10% 20%, rgba(255,255,255,0.4) 0%, transparent 40%)',
        }}>
          <div className="bg-white rounded-lg p-3 shadow-sm max-w-[85%] text-[14px] whitespace-pre-wrap leading-relaxed">
            {rendered}
            <div className="text-[10px] text-slate-400 mt-1.5 text-right">12:34 PM ✓✓</div>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Variables filled with sample data. Real sends use the actual booking / customer data.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Manual Send dialog
// ─────────────────────────────────────────────────────────────
function ManualSendDialog({ templates, onClose, api, onSent }) {
  const [phone, setPhone] = useState('');
  const [templateKey, setTemplateKey] = useState(templates[0]?.key || '');
  const [vars, setVars] = useState({});
  const [sending, setSending] = useState(false);

  const tpl = templates.find((t) => t.key === templateKey);

  const send = async () => {
    if (!phone) { toast.error('Phone number is required'); return; }
    if (!templateKey) { toast.error('Pick a template'); return; }
    setSending(true);
    try {
      await api('POST', '/admin/whatsapp/send', {
        phone,
        template_key: templateKey,
        context: vars,
      });
      toast.success('Message queued — check status in Messages tab');
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="wa-manual-send">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send size={18} /> Send WhatsApp — Manual
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Recipient Phone (with country code, e.g. 919876543210)</label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="919876543210" data-testid="manual-phone" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Template</label>
            <select
              value={templateKey}
              onChange={(e) => { setTemplateKey(e.target.value); setVars({}); }}
              className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm"
              data-testid="manual-template-select"
            >
              {templates.map((t) => (
                <option key={t.key} value={t.key}>{t.name}</option>
              ))}
            </select>
          </div>
          {(tpl?.variables || []).map((v) => (
            <div key={v}>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">{v}</label>
              <Input
                value={vars[v] || ''}
                onChange={(e) => setVars({ ...vars, [v]: e.target.value })}
                data-testid={`manual-var-${v}`}
              />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="manual-send-btn">
            {sending ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
            Send Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// Main tab
// ─────────────────────────────────────────────────────────────
export default function WhatsAppManagementTab({ api, isSuper = false }) {
  const [view, setView] = useState('dashboard');
  const [templates, setTemplates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({});
  const [settings, setSettings] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [waConfig, setWaConfig] = useState({ send_buttons_enabled: true });
  const [editingAccount, setEditingAccount] = useState(null); // null | 'new' | account
  const [testAccount, setTestAccount] = useState(null); // account → test-send dialog
  const [loading, setLoading] = useState(false);
  const [editingTpl, setEditingTpl] = useState(null);
  const [previewTpl, setPreviewTpl] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, m, s, st] = await Promise.all([
        api('GET', '/admin/whatsapp/templates'),
        api('GET', `/admin/whatsapp/messages${statusFilter ? `?status=${statusFilter}` : ''}`),
        api('GET', '/admin/whatsapp/stats'),
        api('GET', '/admin/whatsapp/settings'),
      ]);
      setTemplates(t.data?.templates || []);
      setMessages(m.data?.messages || []);
      setStats(s.data || {});
      setSettings(st.data || {});
      try {
        const cfg = await api('GET', '/admin/whatsapp/config');
        setWaConfig({ send_buttons_enabled: !!cfg.data?.send_buttons_enabled });
        setAccounts(cfg.data?.accounts || []);
      } catch { /* non-super or not ready */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load WhatsApp data');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const saveTemplate = async (draft) => {
    const id = draft.id;
    const patch = {
      name: draft.name,
      campaign_name: draft.campaign_name,
      enabled: draft.enabled,
      auto_send: draft.auto_send,
      body: draft.body,
      variables: draft.variables,
    };
    await api('PATCH', `/admin/whatsapp/templates/${id}`, patch);
    await load();
  };

  const toggleTemplate = async (tpl, field) => {
    try {
      await api('PATCH', `/admin/whatsapp/templates/${tpl.id}`, { [field]: !tpl[field] });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const duplicateTemplate = async (tpl) => {
    try {
      await api('POST', `/admin/whatsapp/templates/${tpl.id}/duplicate`);
      toast.success('Duplicated');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const toggleSendButtons = async (v) => {
    setWaConfig((c) => ({ ...c, send_buttons_enabled: v }));
    try {
      await api('PATCH', '/admin/whatsapp/config', { send_buttons_enabled: v });
      toast.success(v ? 'Send-on-WhatsApp buttons enabled' : 'Send-on-WhatsApp buttons disabled');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
      setWaConfig((c) => ({ ...c, send_buttons_enabled: !v }));
    }
  };

  const saveAccount = async (draft) => {
    const payload = {
      label: draft.label, sender_number: draft.sender_number, plan_label: draft.plan_label,
      status_label: draft.status_label, default_campaign_name: draft.default_campaign_name,
      enabled: draft.enabled, is_default: draft.is_default,
    };
    if (draft.id) {
      if (draft.api_key) payload.api_key = draft.api_key; // only overwrite if typed
      await api('PATCH', `/admin/whatsapp/accounts/${draft.id}`, payload);
    } else {
      payload.api_key = draft.api_key || '';
      await api('POST', '/admin/whatsapp/accounts', payload);
    }
    await load();
  };

  const toggleAccount = async (acct) => {
    try { await api('PATCH', `/admin/whatsapp/accounts/${acct.id}`, { enabled: !acct.enabled }); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const makeDefault = async (acct) => {
    try { await api('PATCH', `/admin/whatsapp/accounts/${acct.id}`, { is_default: true }); toast.success(`${acct.label} is now the default`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const deleteAccount = async (acct) => {
    if (!window.confirm(`Remove AiSensy account "${acct.label}"? This cannot be undone.`)) return;
    try { await api('DELETE', `/admin/whatsapp/accounts/${acct.id}`); toast.success('Account removed'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const filteredMessages = useMemo(() => {
    if (!phoneFilter) return messages;
    const q = phoneFilter.replace(/\D/g, '');
    return messages.filter((m) => (m.phone || '').includes(q));
  }, [messages, phoneFilter]);

  return (
    <div data-testid="whatsapp-mgmt-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MessageCircle size={22} className="text-emerald-600" /> WhatsApp Management
          </h1>
          <div className="text-sm text-slate-500 mt-0.5 flex items-center flex-wrap gap-x-1">
            AiSensy · Sender <span className="font-mono font-semibold text-slate-700">+91 {settings.sender_number || '—'}</span>
            {settings.configured ? (
              <Badge className="ml-2 bg-emerald-100 text-emerald-700 border-emerald-200">API Configured</Badge>
            ) : (
              <Badge className="ml-2 bg-amber-100 text-amber-700 border-amber-200">API Key Missing</Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="wa-refresh">
            <RefreshCw size={14} className="mr-1.5" /> Refresh
          </Button>
          <Button onClick={() => setManualOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-manual-send-open">
            <Send size={14} className="mr-1.5" /> Send Message
          </Button>
        </div>
      </div>

      {/* Sub-nav */}
      <div className="flex gap-1 border-b border-slate-200 mb-5 overflow-x-auto">
        {[
          { id: 'dashboard', label: 'Dashboard', Icon: BarChart3 },
          { id: 'templates', label: `Templates (${templates.length})`, Icon: FileText },
          { id: 'messages', label: `Messages (${stats.total || 0})`, Icon: Activity },
          { id: 'settings', label: 'Settings', Icon: Settings },
        ].map((v) => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            data-testid={`wa-subnav-${v.id}`}
            className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
              view === v.id ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <v.Icon size={14} /> {v.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="text-sm text-slate-500 flex items-center gap-2 mb-3">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      )}

      {/* DASHBOARD */}
      {view === 'dashboard' && (
        <div data-testid="wa-dashboard">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <StatCard label="Total"     value={stats.total}     color="from-slate-700 to-slate-900"      Icon={MessageCircle} />
            <StatCard label="Sent"      value={stats.sent}      color="from-blue-500 to-blue-700"        Icon={Send} />
            <StatCard label="Delivered" value={stats.delivered} color="from-emerald-500 to-emerald-700"  Icon={CheckCircle2} />
            <StatCard label="Read"      value={stats.read}      color="from-purple-500 to-purple-700"    Icon={Eye} />
            <StatCard label="Failed"    value={stats.failed}    color="from-red-500 to-red-700"          Icon={XCircle} />
            <StatCard label="Cancelled" value={stats.cancelled} color="from-amber-500 to-amber-700"      Icon={X} />
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">
            <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-600" /> Setup Checklist
            </h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                {settings.configured
                  ? <CheckCircle2 size={16} className="text-emerald-600 mt-0.5" />
                  : <XCircle size={16} className="text-red-500 mt-0.5" />}
                <span><strong>AiSensy API key configured</strong> — {settings.key_preview || 'Missing'}</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 size={16} className="text-emerald-600 mt-0.5" />
                <span><strong>Sender number</strong> — +91 {settings.sender_number || '—'}</span>
              </li>
              <li className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
                <span>
                  <strong>Activate WhatsApp Business plan in AiSensy dashboard</strong> — current API responses show <em>&quot;No Plan active on assistant&quot;</em>.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
                <span>
                  <strong>Approve 4 templates in AiSensy</strong> matching the campaign names in each row of the <button className="underline text-emerald-700" onClick={() => setView('templates')}>Templates</button> tab — or rename them to your own approved templates.
                </span>
              </li>
            </ul>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Most Recent Messages</h3>
            {messages.slice(0, 5).length === 0 ? (
              <p className="text-sm text-slate-500">No messages yet. They will appear here once leads/bookings start flowing.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {messages.slice(0, 5).map((m) => (
                  <div key={m.id} className="py-2.5 flex items-center gap-3 text-sm">
                    <StatusBadge status={m.status} />
                    <span className="font-mono text-slate-700">+{m.phone}</span>
                    <span className="text-slate-500 hidden sm:inline">·</span>
                    <span className="text-slate-600 truncate">{m.template_key || m.event}</span>
                    <span className="ml-auto text-[11px] text-slate-400">
                      {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TEMPLATES */}
      {view === 'templates' && (
        <div className="space-y-3" data-testid="wa-templates">
          {templates.map((t) => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5" data-testid={`tpl-row-${t.key}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-bold text-slate-900">{t.name}</h3>
                    <Badge className="bg-slate-100 text-slate-600 border-slate-200 font-mono text-[10px]">{t.key}</Badge>
                    {t.enabled ? (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">ENABLED</Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[10px]">DISABLED</Badge>
                    )}
                    {t.auto_send && (
                      <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">AUTO-SEND</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    Event: <code className="font-mono text-slate-700">{t.event}</code> ·
                    AiSensy Campaign: <code className="font-mono text-emerald-700">{t.campaign_name}</code>
                  </p>
                  <p className="text-[13px] text-slate-700 leading-relaxed line-clamp-3 whitespace-pre-wrap">
                    {t.body}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(t.variables || []).map((v) => (
                      <span key={v} className="text-[10px] font-mono bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                        {`{{${v}}}`}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => setPreviewTpl(t)} data-testid={`tpl-preview-${t.key}`}>
                    <Eye size={13} className="mr-1" /> Preview
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingTpl(t)} data-testid={`tpl-edit-${t.key}`}>
                    <Edit3 size={13} className="mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => duplicateTemplate(t)}>
                    <Copy size={13} className="mr-1" /> Duplicate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleTemplate(t, 'enabled')}
                    className={t.enabled ? 'text-slate-600' : 'text-emerald-600'}
                    data-testid={`tpl-toggle-${t.key}`}
                  >
                    {t.enabled
                      ? <><PowerOff size={13} className="mr-1" /> Disable</>
                      : <><Power size={13} className="mr-1" /> Enable</>}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MESSAGES */}
      {view === 'messages' && (
        <div data-testid="wa-messages">
          <div className="flex flex-wrap gap-2 mb-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-slate-200 px-2 text-sm"
              data-testid="msg-status-filter"
            >
              <option value="">All statuses</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="read">Read</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
              <option value="skipped">Skipped</option>
            </select>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                placeholder="Search phone"
                value={phoneFilter}
                onChange={(e) => setPhoneFilter(e.target.value)}
                className="pl-8 h-9 w-48"
                data-testid="msg-phone-filter"
              />
            </div>
          </div>

          {filteredMessages.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
              <MessageCircle size={32} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">No messages match the current filters.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Phone</th>
                    <th className="px-3 py-2 text-left">Template</th>
                    <th className="px-3 py-2 text-left">Event</th>
                    <th className="px-3 py-2 text-left">Sent At</th>
                    <th className="px-3 py-2 text-left">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredMessages.map((m) => (
                    <tr key={m.id} data-testid={`msg-row-${m.id}`}>
                      <td className="px-3 py-2"><StatusBadge status={m.status} /></td>
                      <td className="px-3 py-2 font-mono">+{m.phone}</td>
                      <td className="px-3 py-2">{m.template_key || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{m.event || '—'}</td>
                      <td className="px-3 py-2 text-[11px] text-slate-500">
                        {m.created_at ? new Date(m.created_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-red-600 max-w-xs truncate" title={m.error || ''}>
                        {m.error || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* SETTINGS */}
      {view === 'settings' && (
        <div className="space-y-5 max-w-3xl" data-testid="wa-settings">
          {/* Master toggle */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-slate-900 flex items-center gap-2"><Power size={16} className="text-emerald-600" /> Send-on-WhatsApp Buttons</h3>
              <p className="text-sm text-slate-500 mt-0.5">Master switch for the "Send on WhatsApp" button on invoices &amp; receipts.</p>
            </div>
            <label className="flex items-center gap-2 shrink-0">
              <Switch checked={!!waConfig.send_buttons_enabled} onCheckedChange={toggleSendButtons} disabled={!isSuper} data-testid="wa-send-toggle" />
              <span className="text-sm font-semibold">{waConfig.send_buttons_enabled ? 'On' : 'Off'}</span>
            </label>
          </div>

          {/* Accounts manager */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="wa-accounts">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-slate-900 flex items-center gap-2"><Settings size={16} /> AiSensy Accounts</h3>
                <p className="text-sm text-slate-500 mt-0.5">Add multiple AiSensy numbers; pick which one to send through at send time.</p>
              </div>
              {isSuper && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setEditingAccount('new')} data-testid="wa-account-add"><Send size={13} className="mr-1.5" /> Add Account</Button>}
            </div>
            {accounts.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">No accounts yet. {isSuper ? 'Click "Add Account" to create one.' : 'Ask a super admin to add one.'}</p>
            ) : (
              <div className="space-y-2.5">
                {accounts.map((a) => (
                  <div key={a.id} className="border border-slate-200 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`wa-account-row-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-bold text-slate-900">{a.label}</span>
                        {a.is_default && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">DEFAULT</Badge>}
                        {a.enabled ? <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px]">ENABLED</Badge> : <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[10px]">DISABLED</Badge>}
                        {a.status_label && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">{a.status_label}</Badge>}
                        {a.plan_label && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">{a.plan_label}</Badge>}
                      </div>
                      <div className="text-[11px] text-slate-500 space-x-2">
                        <span>Sender: <b>+91 {a.sender_number || '—'}</b></span>
                        <span>· Key: <span className="font-mono">{a.api_key_preview || '—'}</span></span>
                        <span>· Campaign: <span className="font-mono text-emerald-700">{a.default_campaign_name || '— not set —'}</span></span>
                      </div>
                    </div>
                    {isSuper && (
                      <div className="flex flex-wrap gap-1.5 shrink-0">
                        {!a.is_default && <Button size="sm" variant="outline" onClick={() => makeDefault(a)} data-testid={`wa-account-default-${a.id}`}>Set Default</Button>}
                        <Button size="sm" variant="outline" onClick={() => setTestAccount(a)} className="text-[#25D366] border-emerald-200 hover:bg-emerald-50" data-testid={`wa-account-test-${a.id}`}><Send size={13} className="mr-1" /> Test Send</Button>
                        <Button size="sm" variant="outline" onClick={() => toggleAccount(a)} className={a.enabled ? 'text-slate-600' : 'text-emerald-600'} data-testid={`wa-account-toggle-${a.id}`}>
                          {a.enabled ? <><PowerOff size={13} className="mr-1" /> Disable</> : <><Power size={13} className="mr-1" /> Enable</>}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingAccount(a)} data-testid={`wa-account-edit-${a.id}`}><Edit3 size={13} className="mr-1" /> Edit</Button>
                        <Button size="sm" variant="outline" onClick={() => deleteAccount(a)} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`wa-account-del-${a.id}`}><X size={13} className="mr-1" /> Remove</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* AiSensy invoice template setup guide */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="wa-template-guide">
            <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2"><FileText size={16} className="text-emerald-600" /> How to set up the Invoice/Receipt WhatsApp template</h3>
            <p className="text-sm text-slate-500 mb-3">Create this once in your AiSensy dashboard, then paste its name into each account's <b>Default Campaign Name</b>.</p>
            <ol className="text-[13px] text-slate-700 space-y-2 list-decimal pl-5">
              <li>In AiSensy, go to <b>Templates → Create Template</b> (category <b>UTILITY</b>).</li>
              <li>Set <b>Header = Document</b> (this lets us attach the invoice/receipt PDF).</li>
              <li>Body with 3 variables, e.g.:<br/>
                <span className="block mt-1 p-2 bg-slate-50 rounded font-mono text-[11px] leading-relaxed">Hi {'{{1}}'}, here is your invoice {'{{2}}'} for amount {'{{3}}'}. Thank you for choosing us!</span>
                Variable order sent by the CRM: <b>{'{{1}}'}=Customer name, {'{{2}}'}=Invoice/Receipt no., {'{{3}}'}=Amount</b>.
              </li>
              <li>Submit for approval. Once <b>Approved</b>, create a <b>Campaign</b> using it and copy the <b>campaign name</b>.</li>
              <li>Paste that name into the account's <b>Default Campaign Name</b> (Edit account), then use <b>Test Send</b> to verify.</li>
            </ol>
          </div>

          {/* Provider reference */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2"><Settings size={16} /> Provider</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between border-b pb-2"><span className="text-slate-500">Provider</span><span className="font-semibold">AiSensy</span></div>
              <div className="flex justify-between border-b pb-2"><span className="text-slate-500">API Endpoint</span><span className="font-mono text-xs">{settings.api_url}</span></div>
            </div>
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-[13px] text-amber-900">
              Plan &amp; status labels are maintained manually here (AiSensy has no API to read them). Update each account after verifying in your AiSensy dashboard.
            </div>
          </div>
        </div>
      )}

      {editingTpl && (
        <TemplateEditor
          tpl={editingTpl}
          onClose={() => setEditingTpl(null)}
          onSave={saveTemplate}
        />
      )}
      {previewTpl && (
        <TemplatePreview tpl={previewTpl} onClose={() => setPreviewTpl(null)} />
      )}
      {manualOpen && (
        <ManualSendDialog
          templates={templates.filter((t) => t.enabled)}
          onClose={() => setManualOpen(false)}
          api={api}
          onSent={load}
        />
      )}
      {editingAccount && (
        <AccountEditor
          account={editingAccount === 'new' ? null : editingAccount}
          onClose={() => setEditingAccount(null)}
          onSave={saveAccount}
        />
      )}
      {testAccount && (
        <TestSendDialog
          api={api}
          account={testAccount}
          onClose={() => setTestAccount(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Per-account "Test Send" — sends a sample invoice PDF to validate the campaign
// ─────────────────────────────────────────────────────────────
function TestSendDialog({ api, account, onClose }) {
  const [phone, setPhone] = useState('');
  const [campaign, setCampaign] = useState(account.default_campaign_name || '');
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!phone.trim()) { toast.error('Enter a phone number to test'); return; }
    setSending(true);
    try {
      const r = await api('POST', `/admin/whatsapp/accounts/${account.id}/test-send`, {
        phone: phone.trim(),
        campaign_name: campaign.trim() || undefined,
        public_base: window.location.origin,
      });
      toast.success(`Test invoice sent via ${account.label} → +${r.data?.phone}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Test send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="wa-test-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send size={18} className="text-[#25D366]" /> Test Send — {account.label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-sm text-slate-500">Sends a <b>sample invoice PDF</b> through this account's campaign to confirm your approved AiSensy document template works end-to-end.</p>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Your WhatsApp number</label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="9876543210" data-testid="wa-test-phone" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Campaign name</label>
            <Input value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="approved AiSensy campaign" data-testid="wa-test-campaign" />
            {!account.default_campaign_name && <p className="text-[11px] text-amber-600 mt-1">This account has no default campaign set — enter one above or save it in Edit.</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending} className="bg-[#25D366] hover:bg-[#1da851] text-white" data-testid="wa-test-submit">
            {sending ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />} Send Test
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
// AiSensy Account add/edit dialog
// ─────────────────────────────────────────────────────────────
function AccountEditor({ account, onClose, onSave }) {
  const isNew = !account;
  const [draft, setDraft] = useState(() => ({
    id: account?.id,
    label: account?.label || '',
    api_key: '',
    sender_number: account?.sender_number || '',
    plan_label: account?.plan_label || '',
    status_label: account?.status_label || '',
    default_campaign_name: account?.default_campaign_name || '',
    enabled: account?.enabled ?? true,
    is_default: account?.is_default ?? false,
  }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!draft.label.trim()) { toast.error('Account label is required'); return; }
    if (isNew && !draft.api_key.trim()) { toast.error('API key is required for a new account'); return; }
    setSaving(true);
    try {
      await onSave(draft);
      toast.success(isNew ? 'Account added' : 'Account updated');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="wa-account-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings size={18} /> {isNew ? 'Add AiSensy Account' : `Edit — ${account.label}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Account Label *</label>
            <Input value={draft.label} onChange={(e) => set('label', e.target.value)} placeholder="e.g. Dehradun Branch" data-testid="wa-acc-label" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">
              AiSensy API Key {isNew ? '*' : <span className="font-normal text-slate-400">(leave blank to keep current)</span>}
            </label>
            <Input value={draft.api_key} onChange={(e) => set('api_key', e.target.value)} placeholder={isNew ? 'paste API key' : '•••••• unchanged'} type="password" data-testid="wa-acc-key" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Sender Number</label>
              <Input value={draft.sender_number} onChange={(e) => set('sender_number', e.target.value)} placeholder="7819988010" data-testid="wa-acc-sender" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Default Campaign Name</label>
              <Input value={draft.default_campaign_name} onChange={(e) => set('default_campaign_name', e.target.value)} placeholder="approved AiSensy campaign" data-testid="wa-acc-campaign" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Plan (display)</label>
              <Input value={draft.plan_label} onChange={(e) => set('plan_label', e.target.value)} placeholder="e.g. BASIC (monthly)" data-testid="wa-acc-plan" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">WhatsApp Status (display)</label>
              <Input value={draft.status_label} onChange={(e) => set('status_label', e.target.value)} placeholder="e.g. LIVE" data-testid="wa-acc-status" />
            </div>
          </div>
          <div className="flex items-center gap-6 pt-1">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={!!draft.enabled} onCheckedChange={(v) => set('enabled', v)} data-testid="wa-acc-enabled" />
              <span className="font-medium">Enabled</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={!!draft.is_default} onCheckedChange={(v) => set('is_default', v)} data-testid="wa-acc-default" />
              <span className="font-medium">Default account</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X size={14} className="mr-1" /> Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-acc-save">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
